import { useState } from 'react';
import { 
  Bell, 
  Check, 
  Calendar, 
  AlertCircle, 
  Zap, 
  CheckCheck
} from 'lucide-react';
import { InAppNotification, PushAlertConfig } from '../types/energy';
import { 
  getStoredPushConfig, 
  saveStoredPushConfig, 
  requestBrowserPushPermission
} from '../services/notificationService';
import { Language, translations } from '../i18n/translations';

interface NotificationCenterProps {
  notifications: InAppNotification[];
  onUpdateNotifications: (notifs: InAppNotification[]) => void;
  lang?: Language;
}

export function NotificationCenter({
  notifications,
  onUpdateNotifications,
  lang = 'ja'
}: NotificationCenterProps) {
  const [config, setConfig] = useState<PushAlertConfig>(getStoredPushConfig());
  const [permissionState, setPermissionState] = useState<boolean>(config.browserNotificationsGranted);

  const t = translations[lang];

  const handleTogglePermission = async () => {
    const granted = await requestBrowserPushPermission();
    setPermissionState(granted);
    const updated = { ...config, browserNotificationsGranted: granted };
    setConfig(updated);
    saveStoredPushConfig(updated);
  };

  const handleUpdateConfig = (newConfig: PushAlertConfig) => {
    setConfig(newConfig);
    saveStoredPushConfig(newConfig);
  };

  const handleMarkAllRead = () => {
    const updated = notifications.map((n) => ({ ...n, read: true }));
    onUpdateNotifications(updated);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="space-y-5 pb-8">
      {/* Push Notification Device Permission Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-teal-950/40 border border-slate-800 p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-teal-500/15 border border-teal-500/30 text-teal-400 flex items-center justify-center shrink-0">
              <Bell className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">{t.alertsTitle}</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {t.alertsSubtitle}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-800/80 flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold text-slate-200">{t.devicePermissionTitle}</span>
            <p className="text-[11px] text-slate-400">
              {permissionState ? t.devicePermissionActive : t.devicePermissionPrompt}
            </p>
          </div>

          <button
            type="button"
            onClick={handleTogglePermission}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-colors flex items-center gap-1.5 ${
              permissionState
                ? 'bg-emerald-950 text-emerald-400 border border-emerald-800/50'
                : 'bg-teal-500 hover:bg-teal-400 text-slate-950'
            }`}
          >
            {permissionState ? (
              <>
                <Check className="w-3.5 h-3.5" />
                <span>{t.permissionActiveBtn}</span>
              </>
            ) : (
              <span>{t.enablePushBtn}</span>
            )}
          </button>
        </div>
      </div>

      {/* Billing Cycle Preferences Controls */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-4">
        <h4 className="text-sm font-bold text-white">{t.preferencesTitle}</h4>

        <div className="divide-y divide-slate-800/60 text-xs">
          {/* Billing Cycle Statement Ready */}
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-slate-800 text-teal-400 flex items-center justify-center">
                <Calendar className="w-4 h-4" />
              </div>
              <div>
                <span className="font-semibold text-slate-200">{t.alertNewBillTitle}</span>
                <p className="text-[11px] text-slate-400">{t.alertNewBillDesc}</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.billingCycles}
                onChange={(e) => handleUpdateConfig({ ...config, billingCycles: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
            </label>
          </div>

          {/* Due Reminder */}
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-slate-800 text-amber-400 flex items-center justify-center">
                <AlertCircle className="w-4 h-4" />
              </div>
              <div>
                <span className="font-semibold text-slate-200">{t.alertDueDateTitle}</span>
                <p className="text-[11px] text-slate-400">
                  {t.alertDueDateDesc.replace('{days}', config.dueReminderDaysBefore.toString())}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={config.dueReminderDaysBefore}
                onChange={(e) => handleUpdateConfig({ ...config, dueReminderDaysBefore: Number(e.target.value) })}
                className="bg-slate-950 text-slate-200 border border-slate-700 rounded-lg px-2 py-1 text-xs outline-none"
              >
                <option value={1}>{t.dayBefore1}</option>
                <option value={3}>{t.daysBefore3}</option>
                <option value={5}>{t.daysBefore5}</option>
                <option value={7}>{t.daysBefore7}</option>
              </select>
            </div>
          </div>

          {/* Peak Rate Surge Alerts */}
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-slate-800 text-rose-400 flex items-center justify-center">
                <Zap className="w-4 h-4" />
              </div>
              <div>
                <span className="font-semibold text-slate-200">{t.alertPeakTitle}</span>
                <p className="text-[11px] text-slate-400">{t.alertPeakDesc}</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.peakUsageSurges}
                onChange={(e) => handleUpdateConfig({ ...config, peakUsageSurges: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
            </label>
          </div>

          {/* Monthly Budget Tracker Alert */}
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-slate-800 text-emerald-400 flex items-center justify-center">
                <span className="font-bold text-sm">¥</span>
              </div>
              <div>
                <span className="font-semibold text-slate-200">{t.alertBudgetTitle}</span>
                <p className="text-[11px] text-slate-400">
                  {t.alertBudgetDesc.replace('{limit}', config.monthlyBudgetLimit.toLocaleString())}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.monthlyBudgetAlerts}
                onChange={(e) => handleUpdateConfig({ ...config, monthlyBudgetAlerts: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Notifications Feed */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-bold text-white">{t.recentAlertsTitle}</h4>
            {unreadCount > 0 && (
              <span className="text-[10px] font-bold bg-teal-500 text-slate-950 px-1.5 py-0.5 rounded-full">
                {t.newAlertsCount.replace('{count}', unreadCount.toString())}
              </span>
            )}
          </div>

          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1 transition-colors"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              <span>{t.markAllReadBtn}</span>
            </button>
          )}
        </div>

        <div className="divide-y divide-slate-800/60">
          {notifications.map((item) => {
            let title = item.title;
            let body = item.body;
            if (lang === 'en') {
              if (item.id === 'notif-welcome') {
                title = 'Welcome to SymEnergy Native';
                body = 'Configure your SymEnergy MyPage login details in the Scraper tab to synchronize your smart meter telemetry and billing statements.';
              }
            }

            return (
              <div
                key={item.id}
                className={`py-3.5 flex items-start gap-3 transition-colors px-1 ${
                  item.read ? 'opacity-80' : 'opacity-100'
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 mt-0.5 ${
                    item.type === 'bill_due'
                      ? 'bg-amber-950/60 text-amber-400 border border-amber-800/40'
                      : item.type === 'peak_surge'
                      ? 'bg-rose-950/60 text-rose-400 border border-rose-800/40'
                      : 'bg-teal-950/60 text-teal-400 border border-teal-800/40'
                  }`}
                >
                  {item.type === 'bill_due' ? (
                    <Calendar className="w-4 h-4" />
                  ) : item.type === 'peak_surge' ? (
                    <Zap className="w-4 h-4" />
                  ) : (
                    <Bell className="w-4 h-4" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h5 className="text-xs font-bold text-white truncate">{title}</h5>
                    <span className="text-[10px] text-slate-500 whitespace-nowrap ml-2">
                      {item.timestamp}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    {body}
                  </p>
                  {item.amount && (
                    <div className="mt-1.5">
                      <span className="text-[11px] font-mono font-bold text-teal-400">
                        {t.amountDueTag.replace('{val}', item.amount.toLocaleString())}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
